import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { getAvailableLeaveBalanceForType } from "../../../lib/leaveBalance";
import { cn } from "../../../lib/utils";

const leaveTypeItemClassName = cn(
  "pr-4",
  "[&>span:last-child]:block [&>span:last-child]:w-full",

  "data-[highlighted]:bg-[var(--color-accent)]",
  "data-[highlighted]:text-white",
  "data-[highlighted]:[&_span]:text-inherit",
  "data-[highlighted]:[&_svg]:text-white",

  "focus:bg-[var(--color-accent)]",
  "focus:text-white",
  "focus:[&_span]:text-inherit",
  "focus:[&_svg]:text-white",

  "data-[state=checked]:bg-[var(--color-accent)]",
  "data-[state=checked]:text-white",
  "data-[state=checked]:[&_span]:text-inherit",
  "data-[state=checked]:[&_svg]:text-white"
);

function formatBalanceLabel(balance) {
  if (balance === null) return "—";
  const label = balance === 1 ? "Day" : "Days";
  return `${balance.toFixed(1)} ${label}`;
}

function LeaveTypeRow({ name, balance, inheritColor = false }) {
  const textClass = inheritColor ? "text-inherit" : "text-foreground";

  return (
    <span
      className={cn(
        "flex w-full min-w-0 items-center justify-between gap-6 text-left text-sm font-normal",
        textClass
      )}
    >
      <span className="min-w-0 flex-1 truncate">{name}</span>
      <span className={cn("shrink-0 pr-1", textClass)}>{formatBalanceLabel(balance)}</span>
    </span>
  );
}

export function LeaveTypeSelect({
  leaveTypes,
  leaveBalances,
  value,
  onValueChange,
  disabled,
  loading,
  error,
}) {
  const selectedType = useMemo(
    () => leaveTypes.find((type) => String(type.typeId) === value),
    [leaveTypes, value]
  );

  const selectedBalance = useMemo(
    () => getAvailableLeaveBalanceForType(selectedType, leaveBalances),
    [selectedType, leaveBalances]
  );

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger
        className={cn(
          "h-11 rounded-xl border-[var(--color-text-border)] bg-white text-sm text-left focus:ring-[var(--color-accent)]",
          "[&>span:first-child]:min-w-0 [&>span:first-child]:flex-1 [&>span:first-child]:text-left [&>span:first-child]:line-clamp-none",
          error && "border-[var(--color-error)]"
        )}
      >
        <SelectValue
          placeholder={loading ? "Loading leave types..." : "Select Leave Type"}
          className="text-left"
        >
          {selectedType ? (
            <LeaveTypeRow name={selectedType.leaveName} balance={selectedBalance} />
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="min-w-[var(--radix-select-trigger-width)]">
        {leaveTypes.map((leaveType) => {
          const balance = getAvailableLeaveBalanceForType(leaveType, leaveBalances);
          return (
            <SelectItem
              key={leaveType.typeId}
              value={String(leaveType.typeId)}
              textValue={leaveType.leaveName}
              className={leaveTypeItemClassName}
            >
              <LeaveTypeRow name={leaveType.leaveName} balance={balance} inheritColor />
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
